/**
 * The operator console's rail — Ricoh staff working ACROSS customers.
 *
 * A separate rail, not a group in `ADMIN_NAV`: every entry there is about the
 * customer you are signed into, and these are about all of them. Mixing the
 * two in one rail is how an operator ends up changing the wrong customer.
 *
 * Rail names ENTITIES (DESIGN_GUIDELINES): customers, the access grants into
 * them, and the Ricoh staff who hold platform roles. Licences, Activity and
 * Partners join in later phases (see the plan).
 *
 * Pure and Angular-free so the gating is unit-tested.
 */

/** What the viewer may do in the console, from `viewerPlatformAccess`. */
export interface OperatorGates {
  /** `customer:view` held by name — the customer directory. */
  canViewCustomers: boolean;
  /** `customer:access:manage` from the platform operator — Access, Staff. */
  canManageCustomerAccess: boolean;
}

export interface OperatorNavItem {
  id: 'operator-home' | 'operator-customers' | 'operator-access' | 'operator-staff';
  /** Rail label — the rail is English-only, as `ADMIN_NAV` is. */
  label: string;
  icon: string;
  /** Absolute app path. */
  route: string;
  gate: keyof OperatorGates;
}

export const OPERATOR_NAV: readonly OperatorNavItem[] = [
  {
    id: 'operator-home',
    label: 'Home',
    icon: 'home',
    route: '/operator/home',
    gate: 'canViewCustomers',
  },
  {
    id: 'operator-customers',
    label: 'Customers',
    icon: 'building',
    route: '/operator/customers',
    gate: 'canViewCustomers',
  },
  {
    id: 'operator-access',
    label: 'Customer access',
    icon: 'user-check',
    route: '/operator/access',
    gate: 'canManageCustomerAccess',
  },
  {
    id: 'operator-staff',
    label: 'Support staff',
    icon: 'users',
    route: '/operator/staff',
    gate: 'canManageCustomerAccess',
  },
];

/** The entries this viewer may open. */
export function visibleOperatorNav(gates: OperatorGates): OperatorNavItem[] {
  return OPERATOR_NAV.filter((item) => gates[item.gate]);
}

/** Whether the viewer may open the console at all. */
export function canOpenOperatorConsole(gates: OperatorGates): boolean {
  return visibleOperatorNav(gates).length > 0;
}

/** Where the console's door lands this viewer: the first entry they may open. */
export function operatorHome(gates: OperatorGates): string | null {
  return visibleOperatorNav(gates)[0]?.route ?? null;
}

/** True on any `/operator` URL (query string and fragment ignored). */
export function isOperatorUrl(url: string): boolean {
  return /^\/operator(?:[/?#]|$)/.test(url);
}

/**
 * The rail entry a URL belongs to. The routes are disjoint (no entry's route
 * is a prefix of another's), so the first match is the only one.
 */
export function activeOperatorNav(url: string): OperatorNavItem['id'] | null {
  const path = url.split(/[?#]/)[0];
  return (
    OPERATOR_NAV.find((item) => path === item.route || path.startsWith(`${item.route}/`))?.id ??
    null
  );
}
